import { useState, type FormEvent } from 'react';
import { ApiError, fieldIssues } from '../api';
import { useUnsavedGuard, validForm } from '../ui/unsaved';
import { nipText, type Buyer } from './types';

/** The buyer as on the document. */
export function BuyerView({ buyer }: { buyer: Buyer }) {
  return (
    <address className="inv-party-body">
      <strong>{buyer.name || <span className="todo-mark">brak nazwy</span>}</strong>
      {buyer.addressL1 && <span>{buyer.addressL1}</span>}
      {buyer.addressL2 && <span>{buyer.addressL2}</span>}
      <span>{buyer.nip ? `NIP: ${nipText(buyer.nip)}` : 'bez NIP (osoba prywatna)'}</span>
      {buyer.email && <span className="muted">{buyer.email}</span>}
    </address>
  );
}

const issueOf = (error: unknown, path: string) => (error instanceof ApiError ? fieldIssues(error).find((i) => i.path === path)?.message : undefined);

/**
 * The buyer's fields (a draft, or "Popraw i wyślij ponownie" of a rejected invoice): `onSubmit` gets only what changed;
 * an empty NIP is sent as null (a private person).
 */
export function BuyerEditor({ buyer, label, submitLabel, busy, error, onSubmit, onCancel, extra, guard = true }: { buyer: Buyer; label: string; submitLabel: string; busy: boolean; error: unknown; onSubmit: (patch: Partial<Buyer>) => unknown; onCancel: () => void; extra?: React.ReactNode;
  /** false: submitting is not a save (it sends the invoice again to KSeF) — no question on leaving. */
  guard?: boolean }) {
  const [draft, setDraft] = useState({ ...buyer, nip: buyer.nip ?? '' });
  const set = (patch: Partial<typeof draft>) => setDraft({ ...draft, ...patch });
  const patchOf = (): Partial<Buyer> => {
    const patch: Partial<Buyer> = {};
    for (const key of ['name', 'addressL1', 'addressL2', 'email'] as const) if (draft[key] !== buyer[key]) patch[key] = draft[key];
    const nip = draft.nip.trim() === '' ? null : draft.nip.trim();
    if (nip !== buyer.nip) patch.nip = nip;
    return patch;
  };
  function submit(e: FormEvent) {
    e.preventDefault();
    // a failed save is shown by the card (`error`)
    Promise.resolve(onSubmit(patchOf())).catch(() => undefined);
  }
  // owner 2026-10-23 21:14: leaving with a changed buyer asks first; "Zapisz" waits for the card's save
  const guardRef = useUnsavedGuard({
    dirty: guard && Object.keys(patchOf()).length > 0,
    save: async (el) => {
      if (!validForm(el)) return false;
      await onSubmit(patchOf());
      return true;
    },
  });
  const field = (key: 'name' | 'nip' | 'addressL1' | 'addressL2' | 'email', text: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label className="field">
      <span>{text}</span>
      <input value={draft[key]} onChange={(e) => set({ [key]: e.target.value })} autoComplete="off" {...props} />
      {issueOf(error, `buyer.${key}`) && <span className="field-error">{issueOf(error, `buyer.${key}`)}</span>}
    </label>
  );
  return (
    <form ref={guardRef} className="inv-buyer-form" aria-label={label} onSubmit={submit}>
      {field('name', 'Nazwa nabywcy', { required: true })}
      {field('nip', 'NIP nabywcy', { inputMode: 'numeric', placeholder: 'puste = osoba prywatna' })}
      {field('addressL1', 'Adres (ulica)')}
      {field('addressL2', 'Kod pocztowy i miejscowość')}
      {field('email', 'E-mail nabywcy', { type: 'email', inputMode: 'email', autoCapitalize: 'none' })}
      {extra}
      <div className="actions">
        <button className="btn primary" type="submit" disabled={busy}>
          {submitLabel}
        </button>
        <button className="btn" type="button" onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </form>
  );
}
